import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { AspectRatioWrapper, ResponsiveImage, IconCloseNew } from '../../../components';

import css from './ListingImageGallery.module.css';

const ZOOM_SCALE = 2;
const DOUBLE_TAP_DELAY_MS = 250;

const ListingImageGalleryItem = props => {
  const {
    item,
    aspectWidth,
    aspectHeight,
    isFullscreen,
    setShowNav,
    isMobile,
    totalImages,
    openFullscreen,
    index,
    imageVariants,
    imageSizesMaybe,
    heroPriorityProps,
    intl,
    didSwipeRef,
    onExitFullscreen,
    isZoomed,
    onZoomChange,
    optimizedImages,
  } = props;

  const zoomed = !!isZoomed;
  const zoomFrameRef = useRef(null);
  const lastTapRef = useRef(0);
  const singleTapTimerRef = useRef(null);
  const panStartRef = useRef(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const zoomTransform = useMemo(() => {
    const translate = zoomed ? `translate3d(${pan.x}px, ${pan.y}px, 0)` : 'translate3d(0, 0, 0)';
    const scale = zoomed ? `scale(${ZOOM_SCALE})` : 'scale(1)';
    return `${translate} ${scale}`;
  }, [pan.x, pan.y, zoomed]);

  const clearSingleTapTimer = useCallback(() => {
    if (singleTapTimerRef.current) {
      clearTimeout(singleTapTimerRef.current);
      singleTapTimerRef.current = null;
    }
  }, []);

  const zoomIn = useCallback(() => {
    if (zoomed) return;
    setPan({ x: 0, y: 0 });
    onZoomChange(true);
  }, [onZoomChange, zoomed]);

  const zoomOut = useCallback(() => {
    if (!zoomed) return;
    setPan({ x: 0, y: 0 });
    onZoomChange(false);
  }, [onZoomChange, zoomed]);

  const handleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY_MS) {
      lastTapRef.current = 0;
      clearSingleTapTimer();
      zoomOut();
      return;
    }

    lastTapRef.current = now;
    clearSingleTapTimer();

    if (!zoomed) {
      singleTapTimerRef.current = setTimeout(() => {
        zoomIn();
      }, DOUBLE_TAP_DELAY_MS);
    }
  }, [clearSingleTapTimer, zoomIn, zoomOut, zoomed]);

  const clampPan = useCallback((x, y) => {
    const frame = zoomFrameRef.current;
    if (!frame) return { x, y };
    const rect = frame.getBoundingClientRect();
    const maxX = ((ZOOM_SCALE - 1) * rect.width) / 2;
    const maxY = ((ZOOM_SCALE - 1) * rect.height) / 2;
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  const handleTouchStart = useCallback(
    e => {
      if (!zoomed) return;
      const touch = e.touches?.[0];
      if (!touch) return;
      panStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        panX: pan.x,
        panY: pan.y,
      };
    },
    [pan.x, pan.y, zoomed]
  );

  const handleTouchMove = useCallback(
    e => {
      if (!zoomed || !panStartRef.current) return;
      const touch = e.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - panStartRef.current.x;
      const dy = touch.clientY - panStartRef.current.y;
      const nextPan = clampPan(panStartRef.current.panX + dx, panStartRef.current.panY + dy);
      if (e.cancelable) {
        e.preventDefault();
      }
      setPan(nextPan);
    },
    [clampPan, zoomed]
  );

  const handleTouchEnd = useCallback(() => {
    panStartRef.current = null;
  }, []);

  const videoEl =
    item.isVideo && item.videoUrl ? (
      <video
        className={css.videoItem}
        src={item.videoUrl}
        controls
        playsInline
        onClick={e => e.stopPropagation()}
      />
    ) : null;

  if (isFullscreen && isMobile) {
    return (
      <div className={css.fullscreenMobileWrapper}>
        <div className={css.fullscreenMobileHero}>
          <div
            ref={zoomFrameRef}
            className={classNames(css.fullscreenMobileImageFrame, {
              [css.fullscreenMobileImageFrameZoomed]: zoomed,
            })}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onClick={handleTap}
          >
            <div
              className={css.fullscreenMobileImageInner}
              style={{
                transform: zoomTransform,
                transition: zoomed ? 'none' : 'transform 0.2s ease',
              }}
            >
              {videoEl ? (
                videoEl
              ) : (
                <ResponsiveImage
                  rootClassName={css.fullscreenMobileImage}
                  image={item.image}
                  alt={item.alt}
                  variants={imageVariants}
                  optimizedImages={optimizedImages}
                  imageIndex={item.imageIndex}
                  {...imageSizesMaybe}
                  {...heroPriorityProps}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AspectRatioWrapper
      width={aspectWidth}
      height={aspectHeight}
      className={classNames(css.itemWrapper, { [css.itemWrapperFullscreen]: isFullscreen })}
    >
      <div
        className={css.itemCentering}
        onMouseEnter={() => setShowNav(true)}
        onMouseLeave={() => setShowNav(false)}
        onClick={() => {
          if (didSwipeRef.current) {
            didSwipeRef.current = false;
            return;
          }
          if (isFullscreen) {
            onExitFullscreen();
          } else {
            openFullscreen();
          }
        }}
      >
        {isFullscreen && (
          <div
            role="button"
            className={classNames(css.closeButton)}
            onClick={e => {
              e.stopPropagation();
              onExitFullscreen();
            }}
          >
            <IconCloseNew className={css.iconClose} />
          </div>
        )}
        {!isFullscreen && !isMobile && totalImages > 0 ? (
          <button
            type="button"
            className={css.viewAll}
            onClick={e => {
              e.stopPropagation();
              openFullscreen();
            }}
          >
            {intl.formatMessage(
              { id: 'ListingImageGallery.viewImagesButton' },
              { count: totalImages }
            )}
          </button>
        ) : null}
        {isFullscreen && !isMobile ? (
          <div className={css.index}>
            {index + 1} / {totalImages}
          </div>
        ) : null}
        {videoEl ? (
          videoEl
        ) : (
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            optimizedImages={optimizedImages}
            imageIndex={item.imageIndex}
            {...imageSizesMaybe}
            {...heroPriorityProps}
          />
        )}
      </div>
    </AspectRatioWrapper>
  );
};

export default ListingImageGalleryItem;
